/**
 * /founder/dashboard — what a founder sees about their own company.
 *
 * Shows the company profile, the latest Capital Readiness Score (or a
 * "score me" CTA if none yet), the matches we've curated for them, and
 * a "Share preview link" button that mints a public, tokenised CRS URL
 * the founder can email to advisors / investors.
 */
import React, { useEffect, useState } from 'react';
import {
  Building2, Loader2, Sparkles, ArrowRight, Share2, Copy, Check,
  FileDown, Lock,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import { appendAuthToken } from '../services/auth';
import ResearchSourcesPanel from '../components/ResearchSourcesPanel';
import VerificationTierPanel from '../components/VerificationTierPanel';
import RubricAssessmentPanel from '../components/RubricAssessmentPanel';
import EINPanel from '../components/EINPanel';
import selfServeService, {
  SelfServeCompany, SelfServeMatch, CRSPreview, PaymentsStatus,
} from '../services/selfServeService';


const VERDICT_STYLE: Record<string, string> = {
  strong_fit: 'bg-green-100 text-green-700',
  good_fit: 'bg-emerald-50 text-emerald-700',
  marginal_fit: 'bg-yellow-50 text-yellow-700',
  poor_fit: 'bg-orange-50 text-orange-700',
  no_fit: 'bg-red-50 text-red-700',
};


const tierStyle = (tier: string | null): string => {
  switch (tier) {
    case 'investor_ready': return 'bg-green-100 text-green-700';
    case 'nearly_ready': return 'bg-emerald-50 text-emerald-700';
    case 'developing': return 'bg-yellow-50 text-yellow-700';
    case 'early_stage': return 'bg-orange-50 text-orange-700';
    case 'pre_revenue': return 'bg-red-50 text-red-700';
    default: return 'bg-gray-100 text-gray-600';
  }
};


const scoreColor = (s: number | null): string => {
  if (s === null || s === undefined) return 'bg-gray-100 text-gray-500';
  if (s >= 80) return 'bg-green-100 text-green-700';
  if (s >= 60) return 'bg-yellow-100 text-yellow-700';
  if (s >= 40) return 'bg-orange-100 text-orange-700';
  return 'bg-red-100 text-red-700';
};


const FounderDashboardPage: React.FC = () => {
  const [company, setCompany] = useState<SelfServeCompany | null>(null);
  const [crs, setCRS] = useState<CRSPreview | null>(null);
  const [matches, setMatches] = useState<SelfServeMatch[]>([]);
  const [payments, setPayments] = useState<PaymentsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [unlinked, setUnlinked] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitForm, setSubmitForm] = useState({
    name: '', website: '', industry: '', description: '',
    funding_stage: '', funding_amount_sought: '', team_size: '',
    headquarters: '',
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      const co = await selfServeService.getMyCompany();
      if (co.error) {
        if (co.error.toLowerCase().includes('not linked')) {
          setUnlinked(true);
        } else {
          toast.error(co.error);
        }
      } else if (co.data) {
        setCompany(co.data);
      }
      const [crsRes, matchRes, payRes] = await Promise.all([
        selfServeService.getMyCompanyCRS(),
        selfServeService.listMyCompanyMatches(),
        selfServeService.getPaymentsStatus(),
      ]);
      if (crsRes.data) setCRS(crsRes.data);
      if (matchRes.data) setMatches(matchRes.data);
      if (payRes.data) setPayments(payRes.data);
      setLoading(false);
    })();
  }, []);

  const submitCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!submitForm.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setSubmitting(true);
    const res = await selfServeService.submitMyCompany({
      name: submitForm.name.trim(),
      website: submitForm.website || undefined,
      industry: submitForm.industry || undefined,
      description: submitForm.description || undefined,
      funding_stage: submitForm.funding_stage || undefined,
      funding_amount_sought: submitForm.funding_amount_sought
        ? Number(submitForm.funding_amount_sought) : undefined,
      team_size: submitForm.team_size ? Number(submitForm.team_size) : undefined,
      headquarters: submitForm.headquarters || undefined,
    });
    setSubmitting(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) {
      setCompany(res.data);
      setUnlinked(false);
      toast.success('Company submitted. Research is starting in the background.');
    }
  };

  const startCheckout = async () => {
    const res = await selfServeService.startCRSCheckout();
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data?.already_paid) {
      toast.success('Already unlocked — refreshing.');
      window.location.reload();
      return;
    }
    if (res.data?.url) {
      window.location.href = res.data.url;
    }
  };

  const generateShareLink = async () => {
    const res = await selfServeService.mintMyCRSShareLink();
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) {
      setShareUrl(res.data.url);
      try {
        await navigator.clipboard.writeText(res.data.url);
        setShareCopied(true);
        toast.success('Share link copied to clipboard');
        setTimeout(() => setShareCopied(false), 2500);
      } catch {
        toast.success('Share link generated');
      }
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading…
        </div>
      </DashboardLayout>
    );
  }

  if (unlinked) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-12 px-4">
          <div className="text-center mb-8">
            <Building2 className="w-12 h-12 text-indigo-500 mx-auto" />
            <h1 className="text-2xl font-semibold mt-4">Submit your company</h1>
            <p className="text-gray-600 mt-2 leading-relaxed">
              Tell us about your company. We'll start researching it in the
              background and produce your Capital Readiness Score, then surface
              investor matches that fit your stage and sector.
            </p>
          </div>
          <form onSubmit={submitCompany} className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company name *</label>
              <input
                type="text"
                value={submitForm.name}
                onChange={(e) => setSubmitForm({ ...submitForm, name: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                <input
                  type="url"
                  placeholder="https://"
                  value={submitForm.website}
                  onChange={(e) => setSubmitForm({ ...submitForm, website: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                <input
                  type="text"
                  value={submitForm.industry}
                  onChange={(e) => setSubmitForm({ ...submitForm, industry: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">One-line description</label>
              <textarea
                rows={2}
                value={submitForm.description}
                onChange={(e) => setSubmitForm({ ...submitForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                <select
                  value={submitForm.funding_stage}
                  onChange={(e) => setSubmitForm({ ...submitForm, funding_stage: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">—</option>
                  <option value="pre_seed">Pre-seed</option>
                  <option value="seed">Seed</option>
                  <option value="series_a">Series A</option>
                  <option value="series_b">Series B</option>
                  <option value="series_c">Series C</option>
                  <option value="growth">Growth</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Raise ($)</label>
                <input
                  type="number"
                  value={submitForm.funding_amount_sought}
                  onChange={(e) => setSubmitForm({ ...submitForm, funding_amount_sought: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Team size</label>
                <input
                  type="number"
                  value={submitForm.team_size}
                  onChange={(e) => setSubmitForm({ ...submitForm, team_size: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">HQ location</label>
              <input
                type="text"
                placeholder="City, State"
                value={submitForm.headquarters}
                onChange={(e) => setSubmitForm({ ...submitForm, headquarters: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !submitForm.name.trim()}
              className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : 'Submit company'}
            </button>
          </form>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-semibold flex items-center gap-2">
              <Building2 className="w-7 h-7 text-indigo-500" />
              {company?.name || 'Your company'}
            </h1>
            <p className="text-gray-600 mt-1">
              {company?.industry || ''}
              {company?.is_currently_raising && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-emerald-50 text-emerald-700 rounded">
                  Currently raising
                </span>
              )}
            </p>
          </div>
          {company && (
            <a
              href={appendAuthToken(`/api/v1/companies/${company.id}/report.pdf`)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
              title="Download a PDF briefing of this company (rubric + CRS + verification)"
            >
              <FileDown className="w-4 h-4" />
              Download PDF
            </a>
          )}
        </div>

        {/* CRS card */}
        <section className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                Capital Readiness Score
              </h2>
              {crs ? (
                <div className="mt-3 flex items-end gap-3">
                  <div className={`text-4xl font-semibold w-20 h-20 rounded-lg flex items-center justify-center ${scoreColor(crs.overall_score)}`}>
                    {crs.overall_score?.toFixed(0) ?? '—'}
                  </div>
                  <div>
                    {crs.tier && (
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${tierStyle(crs.tier)}`}>
                        {crs.tier.replace(/_/g, ' ')}
                      </span>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                      Generated {crs.generated_at ? new Date(crs.generated_at).toLocaleDateString() : '—'}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 mt-2 text-sm">
                  No assessment yet. Once your team scores you, the result will appear here.
                </p>
              )}
            </div>
            {crs && (
              <button
                onClick={generateShareLink}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 flex-shrink-0"
                title="Generate a public, no-login link to share this preview"
              >
                {shareCopied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                {shareCopied ? 'Copied!' : 'Share preview link'}
              </button>
            )}
          </div>
          {shareUrl && (
            <div className="mt-3 flex items-center gap-2 text-xs">
              <code className="flex-1 truncate bg-gray-50 px-2 py-1 rounded border border-gray-200">
                {shareUrl}
              </code>
              <button
                onClick={async () => {
                  await navigator.clipboard.writeText(shareUrl);
                  toast.success('Copied');
                }}
                className="p-1 text-gray-500 hover:text-gray-800"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Paywall — basic score is free; gap analysis + roadmap unlock
              after Stripe Checkout. Hide entirely if Stripe isn't
              configured for this deployment. */}
          {crs && payments?.enabled && !crs.unlocked && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <div className="flex items-start gap-3 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <Lock className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-indigo-900">
                    Unlock the detailed gap analysis &amp; improvement roadmap
                  </div>
                  <p className="text-sm text-indigo-800 mt-1">
                    Get a dimension-by-dimension breakdown of where you're
                    investor-ready and a prioritised plan to close the gaps.
                    One-time fee — no subscription.
                  </p>
                  <button
                    onClick={startCheckout}
                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
                  >
                    Unlock for ${(payments.crs_assessment_price_cents / 100).toFixed(0)}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
          {crs && crs.unlocked && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <Check className="w-4 h-4" />
                Detailed assessment unlocked
                {crs.paid_at && (
                  <span className="text-gray-500 text-xs">
                    · {new Date(crs.paid_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          )}
        </section>

        {/* IRS EIN — entity validation. Founder enters; admin verifies. */}
        {company && (
          <EINPanel companyId={company.id} isAdmin={false} />
        )}

        {/* Startup rubric — admin-visible scorecard. Founder also sees
            this so they know how the platform is grading them. */}
        {company && (
          <RubricAssessmentPanel entityKind="company" entityId={company.id} />
        )}

        {/* Sources used to build your profile */}
        {company && (
          <ResearchSourcesPanel entityKind="company" entityId={company.id} />
        )}

        {/* Verification tier impact — what those sources actually verify */}
        {company && (
          <VerificationTierPanel companyId={company.id} />
        )}

        {/* Matches */}
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Investor matches ({matches.length})
          </h2>
          {matches.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
              No matches yet. As we onboard investors that fit your stage and sector,
              they'll appear here.
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
              {matches.map((m) => (
                <Link
                  key={m.id}
                  to={`/founder/matches/${m.id}`}
                  className="flex items-center gap-4 p-4 hover:bg-gray-50"
                >
                  <div className={`flex-shrink-0 w-14 h-14 rounded-lg flex items-center justify-center font-semibold ${scoreColor(m.overall_score)}`}>
                    {m.overall_score ?? '—'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{m.counterparty_name || '(unknown)'}</div>
                    <div className="text-sm text-gray-500 mt-0.5 truncate">
                      {m.ai_review_headline || m.recommended_instrument || ' '}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 text-xs">
                      {m.ai_review_verdict && (
                        <span className={`px-1.5 py-0.5 rounded ${VERDICT_STYLE[m.ai_review_verdict] || 'bg-gray-100 text-gray-600'}`}>
                          <Sparkles className="inline w-3 h-3 mr-0.5" />
                          {m.ai_review_verdict.replace('_', ' ')}
                        </span>
                      )}
                      {m.status && (
                        <span className="text-gray-500">· {m.status.replace('_', ' ')}</span>
                      )}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
};

export default FounderDashboardPage;
